import { gql, useMutation, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { toast } from "sonner";
import {
  CREATE_MEMO_TASK,
  DELETE_MEMO_TASK,
  UPDATE_MEMO_TASK,
} from "@/graphql/mutations";
import type { MemoList, MemoTask } from "@/lib/memoList";

const GET_MEMO_LISTS = gql`
  query GetMemoLists($ownerUserIds: [String!]) {
    memoLists(ownerUserIds: $ownerUserIds) {
      ownerUserId
      ownerName
      totalCount
      completedCount
      inProgressCount
      overdueCount
    }
  }
`;

const GET_MEMO_TASKS = gql`
  query GetMemoTasks($ownerUserId: String!) {
    memoTasks(ownerUserId: $ownerUserId) {
      id
      ownerUserId
      title
      description
      status
      dueDate
      startedAt
      completedAt
      version
      createdByUserId
      createdByName
      viewerCanEdit
    }
  }
`;

export interface MemoTaskMutationResult {
  success: boolean;
  message: string;
  memoTask: MemoTask | null;
}

export interface CreateMemoTaskInput {
  ownerUserId: string;
  title: string;
  description?: string | null;
  dueDate?: string | null;
}

export interface UpdateMemoTaskInput {
  id: string;
  version: number;
  title?: string;
  description?: string | null;
  dueDate?: string | null;
  clearDueDate?: boolean;
  status?: string;
}

const EMPTY_LISTS: MemoList[] = [];
const EMPTY_TASKS: MemoTask[] = [];
const FAILED_MESSAGE = "Tallennus epäonnistui";

export function useMemoLists(ownerUserIds: string[] | null, skip: boolean) {
  const emptySelection = ownerUserIds !== null && ownerUserIds.length === 0;
  const { data, loading, refetch } = useQuery<{ memoLists: MemoList[] }>(GET_MEMO_LISTS, {
    variables: { ownerUserIds },
    skip: skip || emptySelection,
    fetchPolicy: "cache-and-network",
  });
  return {
    lists: emptySelection ? EMPTY_LISTS : (data?.memoLists ?? EMPTY_LISTS),
    loading,
    refetch,
  };
}

export function useMemoTasks(ownerUserId: string | null) {
  const { data, loading, refetch } = useQuery<{ memoTasks: MemoTask[] }>(GET_MEMO_TASKS, {
    variables: { ownerUserId },
    skip: !ownerUserId,
    fetchPolicy: "cache-and-network",
  });
  return { tasks: data?.memoTasks ?? EMPTY_TASKS, loading, refetch };
}

export function useMemoTaskMutations(onChanged: () => void) {
  const [createMutation] = useMutation(CREATE_MEMO_TASK);
  const [updateMutation] = useMutation(UPDATE_MEMO_TASK);
  const [deleteMutation] = useMutation(DELETE_MEMO_TASK);

  const run = useCallback(
    async (
      call: () => Promise<MemoTaskMutationResult | null | undefined>,
    ): Promise<MemoTaskMutationResult> => {
      try {
        const result = await call();
        if (result?.success) {
          onChanged();
          return result;
        }
        const message = result?.message ?? FAILED_MESSAGE;
        toast.error(message);
        return result ?? { success: false, message, memoTask: null };
      } catch (err) {
        const message = err instanceof Error ? err.message : FAILED_MESSAGE;
        toast.error(message);
        return { success: false, message, memoTask: null };
      }
    },
    [onChanged],
  );

  const createMemoTask = useCallback(
    (input: CreateMemoTaskInput) =>
      run(async () => (await createMutation({ variables: { input } })).data?.createMemoTask),
    [run, createMutation],
  );

  const updateMemoTask = useCallback(
    (input: UpdateMemoTaskInput) =>
      run(async () => (await updateMutation({ variables: { input } })).data?.updateMemoTask),
    [run, updateMutation],
  );

  const deleteMemoTask = useCallback(
    (id: string, version: number) =>
      run(
        async () => (await deleteMutation({ variables: { id, version } })).data?.deleteMemoTask,
      ),
    [run, deleteMutation],
  );

  return { createMemoTask, updateMemoTask, deleteMemoTask };
}
